// What a coldroom is holding right now, read off the raw pallet + bin rows —
// per cell, and in the overflow. Extracted from CellPicker's two useMemos so
// the arithmetic that decides which tiles an operator may tap is unit-testable
// rather than only reachable by rendering a picker.
//
// One pass, one source of truth: the cell map and the overflow count come out
// of the SAME walk over the same rows, so a tile saying "free" and a counter
// saying "3 already standing here" can never be reading different data about
// the same room.

/** The subset of a pallet row this needs. */
export interface OccupantPalletRow {
  pallet_id:          string
  coldroom_grid:      string | null
  coldroom_overflow?: boolean | null
}

/** The subset of a bin row this needs. */
export interface OccupantBinRow {
  bin_id:             string
  coldroom_grid:      string | null
  coldroom_overflow?: boolean | null
}

export interface CellState {
  /** The pallet occupying this cell, if any. One per cell, by unique index. */
  palletId?: string
  /** How many bins are stacked here. */
  binCount:  number
}

export interface RoomOccupancy {
  /** Keyed by UPPER-CASED, trimmed cell code — 'a3' and 'A3 ' are one cell. */
  cells:         Map<string, CellState>
  /** Pallets + bins deliberately placed in the room but on no cell. */
  overflowCount: number
}

export interface OccupancyOptions {
  /** The pallet being moved — its own cell should not read as occupied. */
  excludePalletId?: string
  /** The bin being moved — likewise. */
  excludeBinId?:    string
}

export function summariseRoomOccupancy(
  pallets: OccupantPalletRow[],
  bins:    OccupantBinRow[],
  { excludePalletId, excludeBinId }: OccupancyOptions = {},
): RoomOccupancy {
  const cells = new Map<string, CellState>()
  let overflowCount = 0

  const at = (key: string): CellState => {
    const cur = cells.get(key) ?? { binCount: 0 }
    cells.set(key, cur)
    return cur
  }

  // Pallets and bins differ only in what they do to the cell they land on, so
  // that is the only part the caller supplies. The routing — cell, overflow, or
  // neither — is written once, which is the whole point: two copies of it is
  // how a picker starts disagreeing with its own counter.
  //
  // No coldroom_id test here. Both reads are already scoped to ONE room by
  // their query, so "flagged and on no cell" is the overflow of THIS room; a
  // row with a cell is on the grid whatever a stale flag says. A row that is
  // flagged for neither reason — in the room with no cell recorded — is a gap
  // in the record and is deliberately counted by nothing: the picker's tile is
  // about a place somebody chose, and the Coldroom page lists the gaps
  // separately so they read as the fault they are.
  const place = (
    row: { coldroom_grid: string | null; coldroom_overflow?: boolean | null },
    onCell: (cell: CellState) => void,
  ): void => {
    const key = row.coldroom_grid?.trim()
    if (key) { onCell(at(key.toUpperCase())); return }
    if (row.coldroom_overflow) overflowCount += 1
  }

  for (const p of pallets) {
    if (excludePalletId && p.pallet_id === excludePalletId) continue
    place(p, cell => { cell.palletId = p.pallet_id })
  }
  for (const b of bins) {
    if (excludeBinId && b.bin_id === excludeBinId) continue
    place(b, cell => { cell.binCount += 1 })
  }

  return { cells, overflowCount }
}

/** Why a cell cannot take what is being placed — or null when it can. */
type CellBlock = 'pallet' | 'bins' | 'full'

/**
 * Can this cell take a `mode` right now? Returns the REASON it cannot, so the
 * caller can word its own tooltip rather than being handed a boolean it then
 * has to re-derive an explanation for.
 *
 *   pallet  needs a cell holding neither a pallet nor any bins
 *   bin     needs a cell holding no pallet and fewer than `stackLimit` bins
 */
// Internal: callers want `describeCell` (which words the refusal) or
// `countAvailableCells` (which counts them). Exporting the bare predicate
// invites a third phrasing of the same refusal somewhere else.
function cellBlockedFor(
  state: CellState | undefined,
  mode: 'pallet' | 'bin',
  stackLimit: number,
): CellBlock | null {
  if (state?.palletId) return 'pallet'
  const binCount = state?.binCount ?? 0
  if (mode === 'pallet') return binCount > 0 ? 'bins' : null
  return binCount >= stackLimit ? 'full' : null
}

/** How many cells in a `rows × cols` layout could take a `mode`. */
export function countAvailableCells(
  occupancy: RoomOccupancy,
  rows: number,
  cols: number,
  mode: 'pallet' | 'bin',
  stackLimit: number,
): number {
  let n = 0
  for (let r = 0; r < rows; r++) {
    const row = String.fromCharCode(65 + r)
    for (let c = 1; c <= cols; c++) {
      if (cellBlockedFor(occupancy.cells.get(`${row}${c}`), mode, stackLimit) === null) n += 1
    }
  }
  return n
}

/** Everything a picker tile needs to render itself, decided in one place. */
export interface CellTile {
  /** Cannot be picked for this mode. */
  unavailable: boolean
  /** The tooltip — says WHY, never just "unavailable". */
  title:       string
  /** Short face label: 'P', '2B', '3/4'. Null = show the column number. */
  extra:       string | null
}

/**
 * How one cell should present itself. Pulled out of the tile's JSX because the
 * four-way branch is the picker's real logic and it was the largest single
 * contributor to that component's complexity — and because a tooltip that
 * explains the refusal is worth testing, being the only thing an operator gets
 * when a tile will not take their pallet.
 */
export function describeCell(
  cellKey: string,
  state: CellState | undefined,
  mode: 'pallet' | 'bin',
  stackLimit: number,
): CellTile {
  const binCount = state?.binCount ?? 0
  switch (cellBlockedFor(state, mode, stackLimit)) {
    case 'pallet':
      return {
        unavailable: true,
        title: `${cellKey} — occupied by pallet ${state!.palletId!.slice(0, 8)}…`,
        extra: 'P',
      }
    case 'bins':
      return {
        unavailable: true,
        title: `${cellKey} — holds ${binCount} bin(s); cannot mix with a pallet`,
        extra: `${binCount}B`,
      }
    case 'full':
      return {
        unavailable: true,
        title: `${cellKey} — full (${binCount}/${stackLimit} bins)`,
        extra: `${binCount}/${stackLimit}`,
      }
    default:
      // Free — but a partly-filled bin stack still shows how full it is, so an
      // operator can top one up rather than opening a fresh cell.
      return binCount > 0
        ? {
            unavailable: false,
            title: `${cellKey} — ${binCount}/${stackLimit} bins (room for more)`,
            extra: `${binCount}/${stackLimit}`,
          }
        : { unavailable: false, title: `${cellKey} — free`, extra: null }
  }
}
